import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import { PSButton } from '@/components/ui/button';
import { selectUseId } from '@/features/auth/auth.slice';
import { BlockType } from '@/interfaces/models/Knowledge/scenario.interface';

import AddNoteButton from '../../AddNoteButton';
import NodeParentChanger from '../../NodeParentChanger';
import NoteForm, { getNoteSchema, INoteForm } from '../../NoteForm';
import ScenarioHeader from '../../ScenarioHeader';
import { MacBotActionKind } from '../../types';
import ArrowValueIcon from './ArrowValue.icon';
import TextScenarioDeleteButton from './scenario-delete-button';
import ScenarioSide from './scenario-side';
import { ScenarioSimpleTextInput } from './scenario-simple-text-input';
import { TextInputWithHint } from './TextInputWithHint';

interface IFormData extends INoteForm {
  title: string;
  variableName: string;
  value: string;
}

export const MacBotOverrideVariableForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
  config,
}: {
  setShow: (item: boolean) => void;
  itemData: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<any>;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any) => Promise<any>;
  parentId: string | null;
  config: any;
  tree: any[];
}) => {
  const { t } = useTranslation();
  const userId = useAppSelector(selectUseId);

  const [isDelete, setIsDelete] = useState(true);
  const [contextMenuList, setContextMenuList] = useState<any[]>([]);

  const schema = Yup.object({
    title: Yup.string()
      .max(31)
      .required(t('MacBotOverrideVariableForm/key7') + ' ' + t('MacBotMediaForm/scenario_variable_name_required')),
    variableName: Yup.string().required(t('MacBotOverrideVariableForm/scenario_variable_old_value_required')),
    value: Yup.string().required(t('MacBotOverrideVariableForm/scenario_variable_new_value_required')),
    ...getNoteSchema(t),
  });

  const {
    register,
    formState: { errors },
    reset,
    handleSubmit,
    setValue,
    getValues,
    watch,
    control,
  } = useForm<IFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      title: itemData?.title ?? '',
      variableName: itemData?.variableName ?? '',
      value: itemData?.value ?? '',
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: '',
          description: '',
          qc: false,
          author: userId,
        },
      ],
    },
  });
  const { fields } = useFieldArray({
    control,
    name: 'notes',
  });

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
    setShow(false);
  };

  const onSubmit = (data: any) => {
    let value;
    try {
      value = JSON.parse(data.value);
    } catch (ex) {
      value = data.value;
    }
    if (itemData) {
      const _data = {
        ...itemData,
        id: itemData.id,
        type: BlockType.OverrideVariable,
        parent: itemData.parent,
        ...data,
        value,
      };
      changeScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    } else {
      const _data = {
        type: BlockType.OverrideVariable,
        parent: parentId,
        ...data,
        value,
      };
      createScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    }
  };

  useEffect(() => {
    if (config?.metaData?.variables) {
      setContextMenuList([...config.metaData.variables]);
    }
  }, [config]);

  useEffect(() => {
    if (!itemData) {
      setIsDelete(false);
    } else {
      reset({
        title: itemData?.title ?? '',
        variableName: itemData?.variableName ?? '',
        value: itemData?.value ?? '',
        notes: itemData?.notes,
      });
    }
  }, [itemData]);

  return (
    <React.Fragment>
      <ScenarioSide size="md" onSubmit={handleSubmit(onSubmit)}>
        <ScenarioHeader
          type={MacBotActionKind.ShowOverrideVariableForm}
          onClose={() => {
            reset();
          }}
        />

        <div className="md:tw-p-6 tw-p-4 tw-bg-gray-50 tw-flex tw-flex-col tw-gap-y-4 tw-w-full">
          <Row>
            <Col xs={12}>
              <TextInputWithHint
                label={t('MacBotOverrideVariableForm/key7')}
                name="title"
                register={register('title')}
                error={errors.title}
                max={30}
                maxLength={30}
                HintMessage={['MacBotOverrideVariableForm/maxlenght']}
              />
            </Col>
          </Row>
          <Row className="tw-items-center g-md-3 md:tw-gap-y-0 tw-gap-y-3">
            <Col xs={12}>
              <Form.Group>
                <Form.Label htmlFor="" className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                  {t('MacBotOverrideVariableForm/key2')}
                </Form.Label>
                <Form.Select
                  translate="no"
                  className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                  isInvalid={!!errors.variableName}
                  {...register(`variableName`)}
                >
                  <option value={''}>{t('MacBotOverrideVariableForm/key3')}</option>

                  {config?.metaData.variables?.map((item: any) => (
                    <option key={item.variableName} value={item.variableName}>
                      {item.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                  {errors.variableName?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xs={12} className="md:tw-pt-8 md:tw-pb-0 tw-pt-4 tw-pb-4 tw-text-center">
              <ArrowValueIcon text={t('MacBotOverrideVariableForm/key4')} />
            </Col>
            <Col xs={12}>
              <Form.Group className="tw-relative">
                <Form.Label htmlFor="" className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                  {t('MacBotOverrideVariableForm/key5')}
                </Form.Label>
                <ScenarioSimpleTextInput
                  placeholder={''}
                  setValue={text => {
                    setValue(`value`, text);
                  }}
                  value={getValues(`value`).toString() ?? ''}
                  contextMenuList={contextMenuList}
                  isInvalid={!!errors.value}
                />

                <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                  {errors.value?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            <TextScenarioDeleteButton itemData={itemData} tree={tree} deleteHandler={deleteHandler} />

            {itemData?.id && <NodeParentChanger tree={tree} currentId={itemData.id} />}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <PSButton id="btn-override-submit" type="submit" disabled={loading} isLoading={loading} title={t('MacBotResponseForm/btn-02')} />
          </div>
        </div>
        <NoteForm noteFormMethods={{ register, watch, errors, fields, setValue }} />
      </ScenarioSide>
    </React.Fragment>
  );
};
